import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import {
    formatPriceMwk,
    getPlanById,
    PLAN_INCLUDES,
    getTrialPeriodLabel,
} from "../../config/subscriptionPlans";
import { ENABLE_SUBSCRIPTION_ONLINE_PAYMENT } from "../../config/featureFlags";
import { authSuccess } from "../../redux/userRelated/userSlice";
import {
    clearRegistrationCheckout,
    saveRegistrationCheckout,
} from "../../utils/adminRegistration";
import SubscriptionPaymentDialog from "./SubscriptionPaymentDialog";

/**
 * Shown after the registration form — school is created only when the user
 * chooses free trial or pay now (not before).
 */
export default function RegistrationPaymentChoiceModal({
    open,
    pendingRegistration,
    planId,
    onClose,
    onContinueTrial,
    onCreateAccount,
    onPaymentComplete,
    initialResume,
}) {
    const dispatch = useDispatch();
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [adminUser, setAdminUser] = useState(null);
    const [creating, setCreating] = useState(false);
    const [actionError, setActionError] = useState("");
    const resolvedPlanId = planId || pendingRegistration?.subscriptionPlan;

    useEffect(() => {
        if (!open) {
            setPaymentOpen(false);
            setAdminUser(null);
            setCreating(false);
            setActionError("");
            return;
        }

        if (initialResume?.adminUser) {
            setAdminUser(initialResume.adminUser);
            if (initialResume.paymentOpen) {
                setPaymentOpen(true);
            }
        }
    }, [open, initialResume]);

    useEffect(() => {
        if (!paymentOpen || !adminUser || !pendingRegistration) return;
        saveRegistrationCheckout({
            pendingRegistration,
            adminUser,
            planId: resolvedPlanId,
            paymentOpen: true,
        });
    }, [paymentOpen, adminUser, pendingRegistration, resolvedPlanId]);

    if (!open || !pendingRegistration) return null;

    const plan = getPlanById(resolvedPlanId);
    const label = pendingRegistration.schoolName || "Your school";
    const adminEmail = pendingRegistration.email || "";
    const adminName = pendingRegistration.name || label;

    const ensureAccountForUser = async () => {
        if (adminUser) return adminUser;
        const user = await onCreateAccount?.({ persistSession: false });
        if (!user) {
            throw new Error("Could not create your school account.");
        }
        setAdminUser(user);
        return user;
    };

    const handleContinueTrial = async () => {
        setCreating(true);
        setActionError("");
        try {
            clearRegistrationCheckout();
            await onContinueTrial?.();
        } catch (err) {
            setActionError(err?.message || "Could not create your school account.");
        } finally {
            setCreating(false);
        }
    };

    const handlePayNow = async () => {
        if (!ENABLE_SUBSCRIPTION_ONLINE_PAYMENT) {
            await handleContinueTrial();
            return;
        }

        setCreating(true);
        setActionError("");
        try {
            await ensureAccountForUser();
            setPaymentOpen(true);
        } catch (err) {
            setActionError(err?.message || "Could not create your school account.");
        } finally {
            setCreating(false);
        }
    };

    const adminId = adminUser?._id || adminUser?.id;

    return (
        <>
            <Dialog
                open={open && !paymentOpen}
                onClose={creating ? undefined : onClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby="registration-payment-title"
            >
                <DialogTitle id="registration-payment-title" sx={{ pb: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                                color: "primary.main",
                            }}
                        >
                            <RocketLaunchOutlinedIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                Activate your school
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {label} will be created when you start your trial or pay.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {actionError ? (
                        <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
                            {actionError}
                        </Typography>
                    ) : null}

                    <Typography variant="subtitle2" gutterBottom>
                        Selected plan: {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {formatPriceMwk(plan.priceMwk)} ({plan.billingLabel}) · {plan.studentRange}
                    </Typography>

                    <List dense disablePadding sx={{ mb: 2 }}>
                        {PLAN_INCLUDES.map((item) => (
                            <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckCircleOutlineIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={item} primaryTypographyProps={{ variant: "body2" }} />
                            </ListItem>
                        ))}
                    </List>

                    <Typography variant="body2" color="text.secondary">
                        Choose <strong>Continue with free trial</strong> ({getTrialPeriodLabel()} trial after platform verification, no payment today) or{" "}
                        <strong>Pay now</strong> to activate your subscription after verification.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
                    <Button
                        onClick={onClose}
                        disabled={creating}
                        color="inherit"
                        sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                    >
                        Back
                    </Button>
                    <Button
                        onClick={handleContinueTrial}
                        variant="outlined"
                        disabled={creating}
                        sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                    >
                        {creating ? <CircularProgress size={22} /> : "Continue with free trial"}
                    </Button>
                    {ENABLE_SUBSCRIPTION_ONLINE_PAYMENT ? (
                        <Button
                            variant="contained"
                            startIcon={creating ? null : <PhoneAndroidIcon />}
                            onClick={handlePayNow}
                            disabled={creating}
                            sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                        >
                            {creating ? <CircularProgress size={22} color="inherit" /> : "Pay now via mobile money"}
                        </Button>
                    ) : null}
                </DialogActions>
            </Dialog>

            {ENABLE_SUBSCRIPTION_ONLINE_PAYMENT && adminUser && adminId ? (
                <SubscriptionPaymentDialog
                    open={paymentOpen}
                    onClose={() => {
                        setPaymentOpen(false);
                        clearRegistrationCheckout();
                    }}
                    adminId={adminId}
                    planId={plan.id}
                    schoolName={label}
                    adminEmail={adminEmail}
                    adminName={adminName}
                    onPaymentConfirmed={() => {
                        clearRegistrationCheckout();
                        dispatch(authSuccess(adminUser));
                        setPaymentOpen(false);
                        onPaymentComplete?.();
                    }}
                />
            ) : null}
        </>
    );
}
